import { Plus } from "lucide-react";
import { useState } from "react";

import type { AdoptionDecision, AdoptionLane } from "@/lib/adoptions";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Tooltip } from "@/ui/Tooltip";

import { AvailableRow } from "./AdoptionRow";
import type { SectionAdoptionCandidate } from "./SectionAdoptionBlock";

/**
 * Lovable's top-right `+ Adopt from library`, for the sections whose candidates
 * come from the shared catalog (#909).
 *
 * ## WHAT MOVED, AND WHAT DID NOT
 *
 * Until this ticket the same capability was a block at the BOTTOM of every
 * section: an `Adopt into <section> from the shared clinical library` heading, a
 * paragraph explaining whole-set replacement, then the candidate rows. The
 * reference screen puts one right-aligned action at the TOP instead, and a
 * clinical author looking for "how do I add a BG domain" reads a heading and a
 * contract paragraph before finding it.
 *
 * WHAT DID NOT MOVE IS THE WRITE. `AvailableRow` is unchanged, it still only
 * calls `onDecide`, and `onDecide` still opens `AdoptionDecisionDialog` — #372's
 * confirmation, extended by #897 with the placement-ownership preflight and the
 * replacement statement. This component adds no adoption API, computes no pin
 * set, and performs no compare-and-set of its own: it is a disclosure over the
 * exact rows that already existed. `BgSetAdoptionPicker` keeps its own dialog
 * for the same action, because its selection is a MULTI-select applied through
 * one whole-set replacement and this one is not.
 *
 * The picker closes before the decision opens. Two modal dialogs stacked over
 * each other is a focus trap inside a focus trap, and the confirmation is the
 * one that must own focus — it is where the write is authorised.
 *
 * ## THE THREE CANDIDATE STATES ARE STILL THREE STATES
 *
 * `null` is a catalog that did not answer, `[]` is a catalog that answered with
 * nothing to offer, and a non-empty list is something to adopt. The block this
 * moved out of documents at length why collapsing any two of them is a defect:
 * "the shared library offers nothing further", printed over a read that failed,
 * is an absence claim on no evidence.
 *
 * All three therefore answer IN THE PICKER, in visible text a keyboard and a
 * screen reader reach: `null` says the catalog did not load and that this is
 * not a claim that there is nothing to adopt, `[]` says the library offers
 * nothing further, and a list renders its rows. The distinction survives the
 * move — it is the reason this component takes the candidate list rather than
 * a boolean.
 *
 * THE CONTROL IS NOT DISABLED OVER THE FAILED READ. A refusal a clinical
 * author can only meet by hovering a greyed-out button is the defect #638
 * names: `Button` uses `aria-disabled` precisely so a reason stays reachable,
 * and a tooltip is still the weakest place to put one. What this control opens
 * onto in that state is an explanation of what the service actually did, which
 * is an answer rather than a dead end — and the section's own Details carries
 * the retry that re-reads the catalog.
 */
export function AdoptFromLibraryAction({
  sectionLabel,
  candidates,
  onDecide,
  activation = "adoptions",
}: {
  /** The section being adopted into, for the control's accessible name. */
  sectionLabel: string;
  /**
   * The library items this project may adopt into this section, or `null`
   * where the catalog could not be read. An empty list is an answer.
   */
  candidates: readonly SectionAdoptionCandidate[] | null;
  onDecide: (decision: AdoptionDecision) => void;
  /**
   * Which write adopting here performs — the whole-set replacement, or a
   * server-owned activation (#749 Pathways, #634 Main concern) that registers
   * the pin's runtime coordinate and leaves every other pin unchanged. It
   * changes the sentence inside the picker, never the control.
   */
  activation?: AdoptionLane;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip
        content={`Adopt into ${sectionLabel} from the shared clinical library`}
      >
        <Button
          size="sm"
          // LOVABLE'S OWN LABEL, with nothing appended. A Project route mounts
          // one section pane and therefore one of these, so a name carrying
          // the section would disambiguate nothing that exists and would move
          // the control away from what a speech-input user says. The section
          // is named in the picker's title, where the choice is actually made.
          onClick={() => setOpen(true)}
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          Adopt from library
        </Button>
      </Tooltip>

      {open ? (
        <Dialog open onOpenChange={(next) => setOpen(next)}>
          <DialogContent
            title={`Adopt from library — ${sectionLabel}`}
            description={ADOPTION_SENTENCE[activation]}
          >
            {candidates === null ? (
              <p role="alert" className="py-4 text-sm text-foreground">
                The shared clinical library did not load, so what this project
                could adopt into {sectionLabel} is not known here. This is not a
                statement that there is nothing to adopt.
              </p>
            ) : candidates.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                The shared clinical library offers nothing further to adopt into{" "}
                {sectionLabel}.
              </p>
            ) : (
              <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
                {candidates.map((candidate) => (
                  <AvailableRow
                    key={candidate.asset.id}
                    asset={candidate.asset}
                    refusal={candidate.refusal}
                    onDecide={(decision) => {
                      // The picker is a way IN to the confirmation, never a
                      // second confirmation. It closes first so the decision
                      // dialog owns focus alone; the write itself is entirely
                      // the caller's.
                      setOpen(false);
                      onDecide(decision);
                    }}
                  />
                ))}
              </ul>
            )}
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

/**
 * What adopting one of these candidates actually writes.
 *
 * The same sentences the bottom-of-section block carried, moved with the rows
 * they describe. They differ because the WRITES differ: a Pathway, Main-concern,
 * Closing or background adoption is a server-owned activation that registers a
 * runtime coordinate, an enabler pair or a variant selection and touches no
 * other family's pin, and describing that as "rewrites this project's whole pin
 * set" would be the confirmation and the sentence above it disagreeing about one
 * write.
 */
const ADOPTION_SENTENCE = {
  adoptions:
    "Adopting one names an immutable version and rewrites this project's whole pin set in a single replacement. The change is described before it is written.",
  "pathway-activation":
    "Adopting one names an immutable version. The Editor Service registers the Pathway's runtime coordinate in this project's configuration with the same write and keeps every other adoption unchanged. The change is described before it is written.",
  "main-concern-activation":
    "Adopting one names an immutable version. The Editor Service registers the Main concern's runtime coordinate in this project's configuration with the same write — a project has exactly one active Main concern, so adopting one replaces the pin this project holds — and keeps every other adoption unchanged. The change is described before it is written.",
  // #867. The closing family's registration is a PAIR the pinned engine refuses
  // to load with only one half present, so the sentence says what the write
  // enables rather than naming a coordinate — and says outright that it is not a
  // diagnostic selection, because that is the confusion the S7 screen contract
  // asks this surface to prevent.
  "closing-activation":
    "Adopting one names an immutable version. The Editor Service wires this project's closing node with the same write, so the session runs the closing conversation after its final tracks, and keeps every other adoption unchanged. A project has exactly one closing conversation, and it is never a diagnostic pathway. The change is described before it is written.",
  "background-activation":
    "Adopting one names an immutable version. The Editor Service selects this project's interview profile and its background-area order from the complete set of background pins with the same write, and keeps every other adoption unchanged. The change is described before it is written.",
} as const satisfies Record<AdoptionLane, string>;
